import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { API_URL, formatCurrency, formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Zap, CheckCircle2, FileText, Loader2 } from 'lucide-react';

const PortalQuotePage = () => {
  const { token } = useParams();

  const { data: quote, isLoading, error } = useQuery({
    queryKey: ['portal-quote', token],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/portal/quotes/${token}`);
      if (!res.ok) throw new Error('Devis introuvable');
      return res.json();
    },
  });

  const signMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/portal/quotes/${token}/sign`, { method: 'POST' });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail); }
      return res.json();
    },
    onSuccess: () => toast.success('Devis signé avec succès !'),
    onError: (err) => toast.error(err.message),
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl"><CardContent className="p-8 space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-40 w-full" />
        </CardContent></Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md"><CardContent className="p-8 text-center">
          <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-heading font-bold mb-2">Devis introuvable</h2>
          <p className="text-muted-foreground">Ce lien est invalide ou a expiré.</p>
        </CardContent></Card>
      </div>
    );
  }

  const isSigned = quote.status === 'signed' || quote.status === 'invoiced';
  const canSign = ['draft', 'sent', 'accepted'].includes(quote.status);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-6 py-4">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
            <Zap className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <p className="font-heading font-bold">{quote.company?.name || 'EventPro'}</p>
            <p className="text-xs text-muted-foreground">Portail client</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Quote Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold">Devis {quote.reference}</h1>
            <p className="text-muted-foreground">
              {quote.client?.name && `Pour ${quote.client.name}`}
              {quote.client?.company && ` - ${quote.client.company}`}
            </p>
          </div>
          <Badge className={isSigned ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' : 'bg-blue-500/10 text-blue-600 border-blue-500/20'}>
            {isSigned ? 'Signé' : quote.status === 'sent' ? 'En attente' : quote.status}
          </Badge>
        </div>

        {/* Info */}
        {(quote.start_date || quote.validity_date) && (
          <Card>
            <CardContent className="p-4 flex flex-wrap gap-6 text-sm">
              {quote.start_date && (
                <div><span className="text-muted-foreground">Période :</span> {formatDate(quote.start_date)}{quote.end_date && ` - ${formatDate(quote.end_date)}`}</div>
              )}
              {quote.validity_date && (
                <div><span className="text-muted-foreground">Valide jusqu'au :</span> {formatDate(quote.validity_date)}</div>
              )}
              <div><span className="text-muted-foreground">Date :</span> {formatDate(quote.created_at)}</div>
            </CardContent>
          </Card>
        )}

        {quote.intro_text && <p className="text-sm text-muted-foreground">{quote.intro_text}</p>}

        {/* Items Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="portal-items-table">
                <thead>
                  <tr className="border-b border-border bg-secondary/40">
                    <th className="text-left p-3 font-medium">Désignation</th>
                    <th className="text-center p-3 font-medium w-20">Qté</th>
                    <th className="text-right p-3 font-medium w-28">P.U.</th>
                    <th className="text-right p-3 font-medium w-28">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {quote.items?.map((item, i) => (
                    <tr key={i} className="border-b border-border/50">
                      <td className="p-3">
                        <p className="font-medium">{item.product_name}</p>
                        {item.description && <p className="text-xs text-muted-foreground">{item.description}</p>}
                      </td>
                      <td className="p-3 text-center">{item.quantity}</td>
                      <td className="p-3 text-right">{formatCurrency(item.unit_price)}</td>
                      <td className="p-3 text-right font-medium">{formatCurrency(item.quantity * item.unit_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Totals */}
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Total HT</span>
              <span className="font-medium">{formatCurrency(quote.total_ht)}</span>
            </div>
            {quote.total_ttc !== quote.total_ht && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total TTC</span>
                <span className="font-medium">{formatCurrency(quote.total_ttc)}</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between text-lg">
              <span className="font-heading font-bold">Total</span>
              <span className="font-heading font-bold text-primary">{formatCurrency(quote.total_ttc || quote.total_ht)}</span>
            </div>
          </CardContent>
        </Card>

        {quote.footer_note && <p className="text-sm text-muted-foreground">{quote.footer_note}</p>}
        {quote.payment_terms && <p className="text-xs text-muted-foreground">Conditions : {quote.payment_terms}</p>}

        {/* Sign Button */}
        {canSign && (
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="p-6 text-center space-y-4">
              <h3 className="font-heading font-bold text-lg">Accepter ce devis</h3>
              <p className="text-sm text-muted-foreground">En signant, vous acceptez les conditions de ce devis.</p>
              <Button
                size="lg" className="px-8"
                onClick={() => signMutation.mutate()}
                disabled={signMutation.isPending}
                data-testid="portal-sign-btn"
              >
                {signMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Signer le devis
              </Button>
            </CardContent>
          </Card>
        )}

        {isSigned && (
          <Card className="border-emerald-500/30 bg-emerald-500/5">
            <CardContent className="p-6 text-center">
              <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-2" />
              <h3 className="font-heading font-bold text-lg text-emerald-600">Devis signé</h3>
              <p className="text-sm text-muted-foreground">Merci ! Nous reviendrons vers vous rapidement.</p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground pt-4">
          {quote.company?.name && <p>{quote.company.name}</p>}
          {quote.company?.address && <p>{quote.company.address} {quote.company.postal_code} {quote.company.city}</p>}
          {quote.company?.phone && <p>Tél : {quote.company.phone}</p>}
        </div>
      </main>
      <Toaster richColors position="top-center" />
    </div>
  );
};

export default PortalQuotePage;
